import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { eventService } from '../../services/api';
import { formatDistanceToNow, format } from 'date-fns';
import {
    FiPlus, FiCalendar, FiMapPin, FiClock, FiUsers, FiArrowLeft,
    FiEdit2, FiTrash2, FiX, FiMessageCircle, FiCheck, FiLink, FiImage
} from 'react-icons/fi';
import './Events.css';
import PhotoUpload from '../../components/PhotoUpload/PhotoUpload';
import PhotoLightbox from '../../components/PhotoLightbox/PhotoLightbox';

const EVENT_TYPES = [
    { value: 'gathering', label: '🏠 Family Gathering' },
    { value: 'birthday', label: '🎂 Birthday' },
    { value: 'holiday', label: '🎄 Holiday' },
    { value: 'wedding', label: '💒 Wedding' },
    { value: 'anniversary', label: '💍 Anniversary' },
    { value: 'graduation', label: '🎓 Graduation' },
    { value: 'memorial', label: '🕯️ Memorial' },
    { value: 'reunion', label: '👨‍👩‍👧‍👦 Reunion' },
    { value: 'other', label: '📅 Other' },
];

const Events = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('upcoming');
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [loadingEvent, setLoadingEvent] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);

    const fetchEvents = useCallback(async () => {
        try {
            setLoading(true);
            const response = activeTab === 'upcoming'
                ? await eventService.getUpcomingEvents()
                : await eventService.getPastEvents();
            if (response.success) {
                setEvents(response.data);
            }
        } catch (err) {
            console.error('Failed to fetch events:', err);
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const location = useLocation();

    useEffect(() => {
        setSelectedEvent(null);
        setShowCreateForm(false);
        setShowEditForm(false);
    }, [location.key]);

    const handleViewEvent = async (eventId) => {
        try {
            setLoadingEvent(true);
            const response = await eventService.getEvent(eventId);
            if (response.success) {
                setSelectedEvent(response.data);
            }
        } catch (err) {
            console.error('Failed to fetch event:', err);
        } finally {
            setLoadingEvent(false);
        }
    };

    const handleDelete = async (eventId) => {
        if (!window.confirm('Are you sure you want to delete this event?')) return;
        try {
            const response = await eventService.deleteEvent(eventId);
            if (response.success) {
                setEvents(prev => prev.filter(e => e.eventID !== eventId));
                setSelectedEvent(null);
            }
        } catch (err) {
            console.error('Failed to delete event:', err);
        }
    };

    const handleEventCreated = (newEvent) => {
        setEvents(prev => [newEvent, ...prev]);
        setShowCreateForm(false);
        setSelectedEvent(newEvent);
    };

    const handleEventUpdated = (updatedEvent) => {
        setEvents(prev => prev.map(e => e.eventID === updatedEvent.eventID ? updatedEvent : e));
        setSelectedEvent(updatedEvent);
        setShowEditForm(false);
    };

    const handleRSVPUpdated = async (eventId) => {
        // Refresh the detail view
        const response = await eventService.getEvent(eventId);
        if (response.success) {
            setSelectedEvent(response.data);
            // Also update the event in the list
            setEvents(prev => prev.map(e =>
                e.eventID === eventId
                    ? { ...e, attendeeCount: response.data.attendeeCount, currentUserRSVP: response.data.currentUserRSVP }
                    : e
            ));
        }
    };

    const getEventTypeEmoji = (type) => {
        const found = EVENT_TYPES.find(t => t.value === type);
        return found ? found.label.split(' ')[0] : '📅';
    };

    const formatEventDate = (startDate, endDate, allDay) => {
        const start = new Date(startDate);
        if (allDay) {
            if (endDate) {
                const end = new Date(endDate);
                return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
            }
            return format(start, 'EEEE, MMMM d, yyyy');
        }
        if (endDate) {
            const end = new Date(endDate);
            if (format(start, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd')) {
                return `${format(start, 'EEEE, MMMM d, yyyy')} · ${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
            }
            return `${format(start, 'MMM d, h:mm a')} - ${format(end, 'MMM d, h:mm a, yyyy')}`;
        }
        return `${format(start, 'EEEE, MMMM d, yyyy')} at ${format(start, 'h:mm a')}`;
    };

    const canEdit = (ev) => user?.memberID === ev.createdByMemberID || user?.isAdmin;

    // Detail View
    if (selectedEvent && !showEditForm) {
        return (
            <div className="events-page">
                <div className="container">
                    <button className="back-btn" onClick={() => { setSelectedEvent(null); fetchEvents(); }}>
                        <FiArrowLeft /> Back to Events
                    </button>

                    <EventDetail
                        event={selectedEvent}
                        canEdit={canEdit(selectedEvent)}
                        onEdit={() => setShowEditForm(true)}
                        onDelete={() => handleDelete(selectedEvent.eventID)}
                        onRSVPUpdated={() => handleRSVPUpdated(selectedEvent.eventID)}
                        getEventTypeEmoji={getEventTypeEmoji}
                        formatEventDate={formatEventDate}
                    />
                </div>
            </div>
        );
    }

    // Create/Edit Form
    if (showCreateForm || showEditForm) {
        return (
            <div className="events-page">
                <div className="container">
                    <EventForm
                        event={showEditForm ? selectedEvent : null}
                        onSave={showEditForm ? handleEventUpdated : handleEventCreated}
                        onCancel={() => { setShowCreateForm(false); setShowEditForm(false); }}
                    />
                </div>
            </div>
        );
    }

    // List View
    return (
        <div className="events-page">
            <div className="container">
                <div className="page-header">
                    <h1><FiCalendar className="page-header-icon" /> Family Events</h1>
                    <p className="page-subtitle">Stay connected with family gatherings and celebrations</p>
                </div>

                <div className="events-toolbar">
                    <div className="events-tabs">
                        <button
                            className={`events-tab ${activeTab === 'upcoming' ? 'active' : ''}`}
                            onClick={() => setActiveTab('upcoming')}
                        >
                            Upcoming
                        </button>
                        <button
                            className={`events-tab ${activeTab === 'past' ? 'active' : ''}`}
                            onClick={() => setActiveTab('past')}
                        >
                            Past
                        </button>
                    </div>
                    <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
                        <FiPlus /> Create Event
                    </button>
                </div>

                {loading ? (
                    <div className="loading-state">
                        <span className="spinner spinner-lg"></span>
                        <p>Loading events...</p>
                    </div>
                ) : events.length === 0 ? (
                    <div className="empty-state card">
                        <div className="empty-state-icon">📅</div>
                        <h3 className="empty-state-title">
                            {activeTab === 'upcoming' ? 'No upcoming events' : 'No past events'}
                        </h3>
                        <p>{activeTab === 'upcoming' ? 'Create an event to bring the family together!' : ''}</p>
                        {activeTab === 'upcoming' && (
                            <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
                                <FiPlus /> Create Event
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="events-list">
                        {events.map(ev => (
                            <div
                                key={ev.eventID}
                                className="event-card card"
                                onClick={() => handleViewEvent(ev.eventID)}
                            >
                                <div className="event-card-date">
                                    <span className="event-card-month">{format(new Date(ev.startDate), 'MMM')}</span>
                                    <span className="event-card-day">{format(new Date(ev.startDate), 'd')}</span>
                                </div>
                                <div className="event-card-info">
                                    <h3 className="event-card-title">
                                        {getEventTypeEmoji(ev.eventType)} {ev.title}
                                    </h3>
                                    <div className="event-card-meta">
                                        <span><FiClock /> {formatEventDate(ev.startDate, ev.endDate, ev.allDay)}</span>
                                        {ev.locationName && <span><FiMapPin /> {ev.locationName}</span>}
                                    </div>
                                    <div className="event-card-footer">
                                        <span className="event-card-attendees">
                                            <FiUsers /> {ev.attendeeCount} going
                                        </span>
                                        {ev.currentUserRSVP && (
                                            <span className={`event-card-rsvp rsvp-${ev.currentUserRSVP}`}>
                                                {ev.currentUserRSVP === 'going' ? '✓ Going' :
                                                    ev.currentUserRSVP === 'maybe' ? '? Maybe' : '✗ Not Going'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ============================================
// EVENT DETAIL
// ============================================
const EventDetail = ({ event: ev, canEdit, onEdit, onDelete, onRSVPUpdated, getEventTypeEmoji, formatEventDate }) => {
    const [eventPhotos, setEventPhotos] = useState([]);
    const [showPhotoUpload, setShowPhotoUpload] = useState(false);
    const [eventAlbum, setEventAlbum] = useState(null);
    const [lightboxPhoto, setLightboxPhoto] = useState(null);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const { user } = useAuth();
    const [rsvpStatus, setRsvpStatus] = useState(ev.currentUserRSVP || '');
    const [savingRSVP, setSavingRSVP] = useState(false);
    const [comments, setComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [newComment, setNewComment] = useState('');

    const handleRSVP = async (status) => {
        try {
            setSavingRSVP(true);
            const response = await eventService.rsvp(ev.eventID, { status, guestCount: 0 });
            if (response.success) {
                setRsvpStatus(status);
                onRSVPUpdated();
            }
        } catch (err) {
            console.error('Failed to RSVP:', err);
        } finally {
            setSavingRSVP(false);
        }
    };

    const loadComments = async () => {
        try {
            setLoadingComments(true);
            const response = await eventService.getComments(ev.eventID);
            if (response.success) {
                setComments(response.data);
            }
        } catch (err) {
            console.error('Failed to load comments:', err);
        } finally {
            setLoadingComments(false);
        }
    };

    const handleToggleComments = () => {
        if (!showComments && comments.length === 0) {
            loadComments();
        }
        setShowComments(!showComments);
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;
        try {
            const response = await eventService.addComment(ev.eventID, { content: newComment });
            if (response.success) {
                setComments(prev => [...prev, response.data]);
                setNewComment('');
            }
        } catch (err) {
            console.error('Failed to add comment:', err);
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm('Delete this comment?')) return;
        try {
            const response = await eventService.deleteComment(commentId);
            if (response.success) {
                setComments(prev => prev.filter(c => c.eventCommentID !== commentId));
            }
        } catch (err) {
            console.error('Failed to delete comment:', err);
        }
    };

    useEffect(() => {
        fetchEventPhotos();
    }, [ev.eventID]);

    const fetchEventPhotos = async () => {
        try {
            const response = await eventService.getEventPhotos(ev.eventID);
            if (response.success) {
                setEventPhotos(response.data);
            }
        } catch (err) {
            console.error('Failed to fetch event photos:', err);
        }
    };

    const handleAddPhotos = async () => {
        try {
            const response = await eventService.getOrCreateAlbum(ev.eventID);
            if (response.success) {
                setEventAlbum(response.data);
                setShowPhotoUpload(true);
            }
        } catch (err) {
            console.error('Failed to get event album:', err);
        }
    };

    const handlePhotoUploaded = (newPhoto) => {
        setEventPhotos(prev => [newPhoto, ...prev]);
    };

    const openLightbox = (photo, index) => {
        setLightboxPhoto(photo);
        setLightboxIndex(index);
    };

    const closeLightbox = () => setLightboxPhoto(null);

    const navigateLightbox = (direction) => {
        const newIndex = lightboxIndex + direction;
        if (newIndex >= 0 && newIndex < eventPhotos.length) {
            setLightboxIndex(newIndex);
            setLightboxPhoto(eventPhotos[newIndex]);
        }
    };

    const getInitials = (firstName, lastName) => {
        return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
    };

    // Group attendees by status
    const going = ev.attendees?.filter(a => a.rsvpStatus === 'going') || [];
    const maybe = ev.attendees?.filter(a => a.rsvpStatus === 'maybe') || [];
    const notGoing = ev.attendees?.filter(a => a.rsvpStatus === 'not_going') || [];

    return (
        <div className="event-detail card">
            <div className="card-body">
                <div className="event-detail-header">
                    <h1>{getEventTypeEmoji(ev.eventType)} {ev.title}</h1>

                    {canEdit && (
                        <div className="event-detail-actions">
                            <button className="btn btn-secondary btn-sm" onClick={onEdit}>
                                <FiEdit2 /> Edit
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={onDelete}>
                                <FiTrash2 /> Delete
                            </button>
                        </div>
                    )}
                </div>

                <div className="event-detail-info">
                    <div className="event-info-item">
                        <FiClock />
                        <span>{formatEventDate(ev.startDate, ev.endDate, ev.allDay)}</span>
                    </div>
                    {(ev.locationName || ev.address) && (
                        <div className="event-info-item">
                            <FiMapPin />
                            <span>
                                {[ev.locationName, ev.address].filter(Boolean).join(' · ')}
                            </span>
                        </div>
                    )}
                    {ev.virtualMeetingURL && (
                        <div className="event-info-item">
                            <FiLink />
                            <a href={ev.virtualMeetingURL} target="_blank" rel="noopener noreferrer">
                                Join Virtual Meeting
                            </a>
                        </div>
                    )}
                </div>

                {ev.description && (
                    <div className="event-description">
                        <p>{ev.description}</p>
                    </div>
                )}

                <div className="event-detail-meta">
                    <span>Created by {ev.createdByMember?.fullName}</span>
                </div>

                {/* RSVP Section */}
                <div className="event-rsvp-section">
                    <h3>Your RSVP</h3>
                    <div className="rsvp-buttons">
                        <button
                            className={`rsvp-btn ${rsvpStatus === 'going' ? 'active going' : ''}`}
                            onClick={() => handleRSVP('going')}
                            disabled={savingRSVP}
                        >
                            <FiCheck /> Going
                        </button>
                        <button
                            className={`rsvp-btn ${rsvpStatus === 'maybe' ? 'active maybe' : ''}`}
                            onClick={() => handleRSVP('maybe')}
                            disabled={savingRSVP}
                        >
                            ? Maybe
                        </button>
                        <button
                            className={`rsvp-btn ${rsvpStatus === 'not_going' ? 'active not-going' : ''}`}
                            onClick={() => handleRSVP('not_going')}
                            disabled={savingRSVP}
                        >
                            <FiX /> Can't Go
                        </button>
                    </div>
                </div>

                {/* Attendees */}
                <div className="event-attendees-section">
                    <h3><FiUsers /> Attendees</h3>

                    {going.length > 0 && (
                        <div className="attendee-group">
                            <h4>Going ({going.length})</h4>
                            <div className="attendee-list">
                                {going.map(a => (
                                    <Link key={a.attendeeID} to={`/member/${a.memberID}`} className="attendee-item">
                                        {a.member?.profilePhotoURL ? (
                                            <img src={a.member.profilePhotoURL} alt={a.member.fullName} className="avatar avatar-sm" />
                                        ) : (
                                            <div className="avatar avatar-sm">
                                                {getInitials(a.member?.firstName, a.member?.lastName)}
                                            </div>
                                        )}
                                        <span>{a.member?.fullName}</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {maybe.length > 0 && (
                        <div className="attendee-group">
                            <h4>Maybe ({maybe.length})</h4>
                            <div className="attendee-list">
                                {maybe.map(a => (
                                    <Link key={a.attendeeID} to={`/member/${a.memberID}`} className="attendee-item">
                                        {a.member?.profilePhotoURL ? (
                                            <img src={a.member.profilePhotoURL} alt={a.member.fullName} className="avatar avatar-sm" />
                                        ) : (
                                            <div className="avatar avatar-sm">
                                                {getInitials(a.member?.firstName, a.member?.lastName)}
                                            </div>
                                        )}
                                        <span>{a.member?.fullName}</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {notGoing.length > 0 && (
                        <div className="attendee-group">
                            <h4>Can't Go ({notGoing.length})</h4>
                            <div className="attendee-list">
                                {notGoing.map(a => (
                                    <Link key={a.attendeeID} to={`/member/${a.memberID}`} className="attendee-item">
                                        {a.member?.profilePhotoURL ? (
                                            <img src={a.member.profilePhotoURL} alt={a.member.fullName} className="avatar avatar-sm" />
                                        ) : (
                                            <div className="avatar avatar-sm">
                                                {getInitials(a.member?.firstName, a.member?.lastName)}
                                            </div>
                                        )}
                                        <span>{a.member?.fullName}</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {going.length === 0 && maybe.length === 0 && notGoing.length === 0 && (
                        <p className="no-attendees">No RSVPs yet. Be the first!</p>
                    )}
                </div>

                {/* Event Photos */}
                <div className="event-photos-section">
                    <div className="event-photos-header">
                        <h3><FiImage /> Event Photos</h3>
                        <button className="btn btn-secondary btn-sm" onClick={handleAddPhotos}>
                            <FiPlus /> Add Photos
                        </button>
                    </div>

                    {eventPhotos.length === 0 ? (
                        <p className="no-event-photos">No photos yet. Be the first to share!</p>
                    ) : (
                        <div className="event-album-groups">
                            {Object.values(
                                eventPhotos.reduce((groups, photo) => {
                                    const key = photo.memberID;
                                    if (!groups[key]) groups[key] = { member: photo.member, photos: [] };
                                    groups[key].photos.push(photo);
                                    return groups;
                                }, {})
                            ).map((group, groupIndex, allGroups) => (
                                <div key={group.member?.memberID} className="event-album-group">
                                    <div className="event-album-group-header">
                                        <span className="event-album-group-name">
                                            {group.member?.fullName || 'Unknown'}
                                        </span>
                                    </div>
                                    <div className="event-photos-grid">
                                        {group.photos.map((photo) => {
                                            const index = eventPhotos.findIndex(p => p.photoID === photo.photoID);
                                            return (
                                                <div
                                                    key={photo.photoID}
                                                    className="event-photo-item"
                                                    onClick={() => openLightbox(photo, index)}
                                                >
                                                    <img src={photo.thumbnailURL || photo.fileURL} alt={photo.caption || 'Event photo'} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {groupIndex < allGroups.length - 1 && <hr className="event-album-divider" />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Comments */}
                <div className="event-comments-section">
                    <button className="event-comments-toggle" onClick={handleToggleComments}>
                        <FiMessageCircle /> Comments {comments.length > 0 ? `(${comments.length})` : ev.commentCount > 0 ? `(${ev.commentCount})` : ''}
                    </button>

                    {showComments && (
                        <div className="event-comments">
                            {loadingComments ? (
                                <div className="loading-state"><span className="spinner spinner-sm"></span> Loading...</div>
                            ) : (
                                <>
                                    {comments.map(c => (
                                        <div key={c.eventCommentID} className="event-comment">
                                            <Link to={`/member/${c.memberID}`}>
                                                {c.member?.profilePhotoURL ? (
                                                    <img src={c.member.profilePhotoURL} alt="" className="avatar avatar-sm" />
                                                ) : (
                                                    <div className="avatar avatar-sm">
                                                        {getInitials(c.member?.firstName, c.member?.lastName)}
                                                    </div>
                                                )}
                                            </Link>
                                            <div className="event-comment-body">
                                                <div className="event-comment-bubble">
                                                    <strong>{c.member?.fullName}</strong>
                                                    <p>{c.content}</p>
                                                </div>
                                                <div className="event-comment-meta">
                                                    <span>{c.createdDate && formatDistanceToNow(new Date(c.createdDate), { addSuffix: true })}</span>
                                                    {c.memberID === user?.memberID && (
                                                        <button className="comment-action comment-action-delete" onClick={() => handleDeleteComment(c.eventCommentID)}>Delete</button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    <form className="event-comment-form" onSubmit={handleAddComment}>
                                        <input
                                            type="text"
                                            className="comment-input"
                                            placeholder="Write a comment..."
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                        />
                                    </form>
                                </>
                            )}
                        </div>
                    )}
                </div>
                {/* Comments */}
                <div className="event-comments-section">
                    ... comments stuff ...
                </div>

                {showPhotoUpload && eventAlbum && (
                    <PhotoUpload
                        onClose={() => setShowPhotoUpload(false)}
                        onUploaded={handlePhotoUploaded}
                        albums={[eventAlbum]}
                        defaultAlbum={eventAlbum.albumID}
                    />
                )}

                {lightboxPhoto && (
                    <PhotoLightbox
                        photo={lightboxPhoto}
                        onClose={closeLightbox}
                        onPrev={() => navigateLightbox(-1)}
                        onNext={() => navigateLightbox(1)}
                        hasPrev={lightboxIndex > 0}
                        hasNext={lightboxIndex < eventPhotos.length - 1}
                        onCaptionUpdated={(photoId, newCaption) => {
                            setEventPhotos(prev => prev.map(p => p.photoID === photoId ? { ...p, caption: newCaption } : p));
                            setLightboxPhoto(prev => ({ ...prev, caption: newCaption }));
                        }}
                        onDelete={
                      (lightboxPhoto.memberID === user?.memberID || 
                       ev.createdByMemberID === user?.memberID || 
                       user?.isAdmin)
                        ? (photoId) => {
                            setEventPhotos(prev => prev.filter(p => p.photoID !== photoId));
                            setLightboxPhoto(null);
                          }
                        : null
                    }
                    />
                )}
            </div>
        </div>
    );
};

// ============================================
// EVENT FORM
// ============================================
const EventForm = ({ event: ev, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        title: ev?.title || '',
        description: ev?.description || '',
        eventType: ev?.eventType || 'gathering',
        startDate: ev ? format(new Date(ev.startDate), "yyyy-MM-dd'T'HH:mm") : '',
        endDate: ev?.endDate ? format(new Date(ev.endDate), "yyyy-MM-dd'T'HH:mm") : '',
        allDay: ev?.allDay || false,
        locationName: ev?.locationName || '',
        address: ev?.address || '',
        virtualMeetingURL: ev?.virtualMeetingURL || '',
        visibility: ev?.visibility || 'family'
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim() || !formData.startDate) {
            setError('Title and start date are required.');
            return;
        }

        try {
            setSaving(true);
            setError('');

            const payload = {
                ...formData,
                startDate: new Date(formData.startDate).toISOString(),
                endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null
            };

            let response;
            if (ev) {
                response = await eventService.updateEvent(ev.eventID, payload);
            } else {
                response = await eventService.createEvent(payload);
            }

            if (response.success) {
                onSave(response.data);
            } else {
                setError(response.message || 'Failed to save event.');
            }
        } catch (err) {
            setError('Failed to save event. Please try again.');
            console.error('Failed to save event:', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="event-form card">
            <div className="card-body">
                <h2>{ev ? 'Edit Event' : 'Create Event'}</h2>
                {error && <div className="form-error-message">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label className="form-label">Event Title *</label>
                        <input type="text" name="title" className="form-input"
                            placeholder="e.g., Family Reunion 2026"
                            value={formData.title} onChange={handleChange} />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Event Type</label>
                        <select name="eventType" className="form-input"
                            value={formData.eventType} onChange={handleChange}>
                            {EVENT_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Description</label>
                        <textarea name="description" className="form-input" rows={3}
                            placeholder="Tell everyone about this event..."
                            value={formData.description} onChange={handleChange} />
                    </div>

                    <div className="form-group">
                        <label className="form-checkbox">
                            <input type="checkbox" name="allDay" checked={formData.allDay} onChange={handleChange} />
                            All day event
                        </label>
                    </div>

                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">Start Date/Time *</label>
                            <input type={formData.allDay ? 'date' : 'datetime-local'} name="startDate"
                                className="form-input" value={formData.allDay ? formData.startDate.split('T')[0] : formData.startDate}
                                onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">End Date/Time</label>
                            <input type={formData.allDay ? 'date' : 'datetime-local'} name="endDate"
                                className="form-input" value={formData.allDay ? formData.endDate.split('T')[0] : formData.endDate}
                                onChange={handleChange} />
                        </div>
                    </div>

                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">Location Name</label>
                            <input type="text" name="locationName" className="form-input"
                                placeholder="e.g., Grandma's House"
                                value={formData.locationName} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Address</label>
                            <input type="text" name="address" className="form-input"
                                placeholder="123 Main St, City, State"
                                value={formData.address} onChange={handleChange} />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Virtual Meeting URL</label>
                        <input type="url" name="virtualMeetingURL" className="form-input"
                            placeholder="https://zoom.us/j/..."
                            value={formData.virtualMeetingURL} onChange={handleChange} />
                    </div>

                    <div className="form-actions">
                        <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? 'Saving...' : (ev ? 'Update Event' : 'Create Event')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Events;